import { centerCrop, makeAspectCrop, PixelCrop } from 'react-image-crop';

export const CROPPER_WIDTH = 240;
export const CROPPER_HEIGHT = 80;

const MIN_ZOOM = 0.2; // Minimum zoom multiplier (20% of fit scale)
const MAX_ZOOM = 5; // Maximum zoom multiplier (500% of fit scale)
const DEFAULT_SLIDER_VALUE = 0.5; // Middle position of slider (0 to 1)

const centerAspectCrop = (
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
) => {
  return centerCrop(
    makeAspectCrop(
      {
        unit: 'px',
        width: CROPPER_WIDTH,
        height: CROPPER_HEIGHT,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
};

// Creates a cropped image from the source image and crop area
const createCroppedImage = async (
  image: HTMLImageElement,
  pixelCrop: PixelCrop,
  scale = 1
): Promise<{ blob: Blob; base64: string }> => {
  if (!pixelCrop || !image) {
    throw new Error('Crop or image is null');
  }

  // Create a canvas with fixed output dimensions
  const canvas = document.createElement('canvas');
  canvas.width = CROPPER_WIDTH;
  canvas.height = CROPPER_HEIGHT;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('No 2d context');
  }

  // Clear with white background
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Calculate center of the crop in the displayed image
  const cropCenterX = pixelCrop.x + pixelCrop.width / 2;
  const cropCenterY = pixelCrop.y + pixelCrop.height / 2;

  // Calculate the source coordinates in the natural image
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  // Calculate the offset from the center when zoomed
  const scaleOffsetX = (cropCenterX - image.width / 2) / scale;
  const scaleOffsetY = (cropCenterY - image.height / 2) / scale;

  // Calculate center point in the original-sized image
  const sourceCenterX = (image.width / 2 + scaleOffsetX) * scaleX;
  const sourceCenterY = (image.height / 2 + scaleOffsetY) * scaleY;

  // Calculate source width and height (adjusted for zoom/scale)
  const sourceWidth = (pixelCrop.width / scale) * scaleX;
  const sourceHeight = (pixelCrop.height / scale) * scaleY;

  // Calculate source top-left position from the center coordinates
  const sourceX = sourceCenterX - sourceWidth / 2;
  const sourceY = sourceCenterY - sourceHeight / 2;

  // Draw the cropped image onto the canvas
  try {
    ctx.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      canvas.width,
      canvas.height
    );
  } catch (err) {
    console.error('Canvas drawing error:', err, {
      sourceParams: {
        x: sourceX,
        y: sourceY,
        width: sourceWidth,
        height: sourceHeight,
      },
      imageSize: {
        naturalWidth: image.naturalWidth,
        naturalHeight: image.naturalHeight,
      },
    });
    throw err;
  }

  // Convert canvas to blob and then to base64
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas toBlob result is null'));
          return;
        }

        // to base64
        const reader = new FileReader();
        reader.addEventListener('load', () => {
          if (typeof reader.result === 'string') {
            resolve({
              blob,
              base64: reader.result,
            });
          } else {
            reject(new Error('FileReader result is not a string'));
          }
        });
        reader.addEventListener('error', () => {
          reject(new Error('FileReader error: ' + reader.error?.message));
        });
        reader.readAsDataURL(blob);
      },
      'image/jpeg',
      1
    );
  });
};

/**
 * Convert normalized slider value (0-1) to actual scale
 * @param value - Slider value between 0-1
 * @param fitScale - Base scale that fits image to container
 * @returns Actual scale to apply to the image
 */
const sliderValueToScale = (value: number, fitScale: number): number => {
  if (value <= 0.5) {
    // Convert 0-0.5 range to MIN_ZOOM*fitScale - fitScale
    return (
      fitScale * MIN_ZOOM + (value / 0.5) * (fitScale - fitScale * MIN_ZOOM)
    );
  } else {
    // Convert 0.5-1 range to fitScale - MAX_ZOOM*fitScale
    return fitScale + ((value - 0.5) / 0.5) * (fitScale * MAX_ZOOM - fitScale);
  }
};

export {
  centerAspectCrop,
  createCroppedImage,
  sliderValueToScale,
  DEFAULT_SLIDER_VALUE,
};
